import React, { useEffect, memo, useCallback } from 'react';
import { Grid, Header, Message, Icon } from 'semantic-ui-react';
import { useSharedData, useActorZone } from '../../hooks/core/useZoneHooks';

/**
 * ActorsListEnh component
 * Enhanced version of ActorsList with improved UI matching VoiceActor components
 * Displays a list of voice actors
 * @param {Object} props - Component props
 * @param {Function} props.onActorSelect - Callback when an actor is selected
 * @param {Function} props.onDeleteActor - Callback when an actor delete is requested
 * @returns {JSX.Element} ActorsListEnh component
 */
const ActorsListEnh = memo(({ onActorSelect, onDeleteActor }) => {
  const sharedData = useSharedData();
  const actorZone = useActorZone();

  // Extract data from hooks
  const {
    actors,
    selections,
    loading,
    errors
  } = sharedData;

  // Map to interface
  const actorsArray = Array.isArray(actors) ? actors : [];
  const actorId = selections?.actorId;
  const actorsLoading = loading?.actors ? 'loading' : 'idle';
  const actorsError = errors?.actors;

  // Get functions from shared data
  const fetchActors = sharedData.actions.fetchActors;
  const setActorSelection = sharedData.actions.setActorSelection;

  // Fetch actors when the component mounts
  useEffect(() => {
    fetchActors();
  }, [fetchActors]);

  // Handle actor selection
  const handleActorSelect = useCallback((actor) => {
    console.log('[VA_ACTOR_LIST Actor selected', actor);
    setActorSelection(actor.uuid);
    if (onActorSelect) {
      onActorSelect(actor);
    }
  }, [setActorSelection, onActorSelect]);

  // Handle delete actor
  const handleDeleteActor = useCallback((e, actor) => {
    e.stopPropagation(); // Prevent row selection
    console.log('[VA_ACTORS_LIST] Delete actor:', actor.name);
    if (onDeleteActor) {
      onDeleteActor(actor);
    }
  }, [onDeleteActor]);

  // Get the status of an actor
  const getActorStatus = useCallback((actor) => {
    return actor.status || 'Active';
  }, []);

  return (
    <div className="va-actors-container">
      <Header className="va-hdr-with-prompt" as="h3">
        <div style={{
          display: 'flex',
          alignItems: 'center',
          height: '100%'
        }}>
          <span>Voice Actors</span>
          {actorsLoading === 'loading' ?
            <div className="va-actor-message" style={{
              backgroundColor: '#F8FFFF',
              border: 'solid 1px #a9d5de',
              marginLeft: '10px'
            }}>
              <p>Loading actors...</p>
            </div>
            : ""}
        </div>
      </Header>
      {/* Actors list */}
      {actorsLoading === 'failed' ? (
        <Message negative>
          <Message.Header>Error Loading Voice Actors</Message.Header>
          <p>{actorsError || 'An unknown error occurred'}</p>
        </Message>
      ) : actors && actors.length > 0 ? (
        <div className="ui segment va-actor-list">
          {/* Header row */}
          <Grid>
            <Grid.Row className="va-list-header-row">
              <Grid.Column className='va-noRightPad' width={9}>Name</Grid.Column>
              <Grid.Column width={7}>Status</Grid.Column>
            </Grid.Row>

            {/* Actor rows */}
            {actors.map((actor) => (
              <Grid.Row
                key={actor.uuid}
                className="va-list-item-row"
                style={{
                  backgroundColor: actorId === actor.uuid ? '#ebf3fb' : 'transparent',
                  cursor: 'pointer'
                }}
                onClick={() => handleActorSelect(actor)}
              >
                <Grid.Column width={9}>{`${actor.firstName} ${actor.lastName}`}</Grid.Column>
                <Grid.Column className='va-noRightPad' width={5}>{getActorStatus(actor)}</Grid.Column>
                <Grid.Column width={2} textAlign="right">
                  <Icon
                    name="trash alternate"
                    color="red"
                    className="va-trash-icon"
                    style={{ cursor: 'pointer' }}
                    onClick={(e) => handleDeleteActor(e, actor)}
                  />
                </Grid.Column>
              </Grid.Row>
            ))}
          </Grid>
        </div>
      ) : (
        <div className="va-empty-list-message">
          <p>No voice actors found. Create a new voice actor to get started.</p>
        </div>
      )}
    </div>
  );
});

export default ActorsListEnh;
